import { UserProgress, Badge, Certification, Course } from '../types';
import { subDays } from 'date-fns';

// Helper function to generate dates relative to today for mock data
function getMockDates() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 30, 0);
  const yesterday = subDays(today, 1);
  const twoDaysAgo = subDays(today, 2);
  const threeDaysAgo = subDays(today, 3);
  const fourDaysAgo = subDays(today, 4);
  const fiveDaysAgo = subDays(today, 5);
  const sixDaysAgo = subDays(today, 6);
  
  return {
    today,
    yesterday,
    twoDaysAgo,
    threeDaysAgo,
    fourDaysAgo,
    fiveDaysAgo,
    sixDaysAgo,
  };
}

// Mock user progress data - will be replaced with real API later
// This generates a 5-day streak (today, yesterday, 2 days ago, 3 days ago, 4 days ago)
export const MOCK_USER_PROGRESS_DATA: UserProgress = (() => {
  const dates = getMockDates();
  
  return {
  badges: [
    { 
      id: 'b1', 
      name: 'Quick Learner', 
      icon: '⚡', 
      description: 'Completed first course in record time', 
      earnedDate: dates.fiveDaysAgo.toISOString()
    },
    { 
      id: 'b2', 
      name: 'Team Player', 
      icon: '🤝', 
      description: 'Helped 5 team members', 
      earnedDate: dates.fourDaysAgo.toISOString()
    },
    { 
      id: 'b3', 
      name: 'Course Master', 
      icon: '🎓', 
      description: 'Completed 5 courses', 
      earnedDate: dates.yesterday.toISOString()
    },
    { 
      id: 'b4', 
      name: 'Early Bird', 
      icon: '🌅', 
      description: 'Completed lessons before 8 AM', 
      earnedDate: dates.threeDaysAgo.toISOString()
    },
    { 
      id: 'b5', 
      name: 'Consistent', 
      icon: '📅', 
      description: '7 day learning streak', 
      earnedDate: dates.twoDaysAgo.toISOString()
    },
    { 
      id: 'b6', 
      name: 'Perfect Score', 
      icon: '💯', 
      description: 'Scored 100% on a quiz', 
      earnedDate: dates.today.toISOString()
    },
    { 
      id: 'b7', 
      name: 'Night Owl', 
      icon: '🌙', 
      description: 'Completed lessons after 10 PM', 
      earnedDate: dates.sixDaysAgo.toISOString()
    },
    { 
      id: 'b8', 
      name: 'Speed Demon', 
      icon: '⚡', 
      description: 'Completed 3 courses in one week', 
      earnedDate: dates.fiveDaysAgo.toISOString()
    },
    { 
      id: 'b9', 
      name: 'Knowledge Seeker', 
      icon: '📚', 
      description: 'Completed 10 hours of learning', 
      earnedDate: dates.fourDaysAgo.toISOString()
    },
    { 
      id: 'b10', 
      name: 'Quiz Master', 
      icon: '✅', 
      description: 'Passed 10 quizzes with 90%+', 
      earnedDate: dates.threeDaysAgo.toISOString()
    },
    { 
      id: 'b11', 
      name: 'Weekend Warrior', 
      icon: '🏋️', 
      description: 'Completed courses on weekends', 
      earnedDate: dates.twoDaysAgo.toISOString()
    },
    { 
      id: 'b12', 
      name: 'First Steps', 
      icon: '👣', 
      description: 'Completed your first course', 
      earnedDate: dates.yesterday.toISOString()
    },
    { 
      id: 'b13', 
      name: 'Dedicated Learner', 
      icon: '🎯', 
      description: '30 days of consistent learning', 
      earnedDate: dates.today.toISOString()
    },
    { 
      id: 'b14', 
      name: 'Social Butterfly', 
      icon: '🦋', 
      description: 'Shared 5 achievements', 
      earnedDate: dates.sixDaysAgo.toISOString()
    },
    { 
      id: 'b15', 
      name: 'Tech Guru', 
      icon: '💻', 
      description: 'Mastered all tech courses', 
      earnedDate: dates.fiveDaysAgo.toISOString()
    },
    { 
      id: 'b16', 
      name: 'Rising Star', 
      icon: '⭐', 
      description: 'Top performer this month', 
      earnedDate: dates.fourDaysAgo.toISOString()
    },
    { 
      id: 'b17', 
      name: 'Problem Solver', 
      icon: '🧩', 
      description: 'Solved 50 practice problems', 
      earnedDate: dates.threeDaysAgo.toISOString()
    },
    { 
      id: 'b18', 
      name: 'Mentor', 
      icon: '👨‍🏫', 
      description: 'Helped 10 learners', 
      earnedDate: dates.twoDaysAgo.toISOString()
    },
  ],
  certifications: [
    { 
      id: 'c1', 
      name: 'React Developer Certification', 
      issuer: 'React Training', 
      completedDate: subDays(dates.today, 30).toISOString(), 
      credentialId: 'REACT-2025-001', 
      status: 'active',
      certificateUrl: 'https://via.placeholder.com/800x600/4A90E2/FFFFFF?text=React+Developer+Certification'
    },
    { 
      id: 'c2', 
      name: 'Advanced JavaScript', 
      issuer: 'JavaScript Institute', 
      completedDate: subDays(dates.today, 45).toISOString(), 
      expiryDate: subDays(dates.today, -320).toISOString(), 
      credentialId: 'JS-ADV-2025-042', 
      status: 'active',
      certificateUrl: 'https://via.placeholder.com/800x600/F39C12/FFFFFF?text=Advanced+JavaScript'
    },
    { 
      id: 'c3', 
      name: 'TypeScript Fundamentals', 
      issuer: 'TypeScript Academy', 
      completedDate: dates.fiveDaysAgo.toISOString(), 
      credentialId: 'TS-FUND-2025-123', 
      status: 'active',
      certificateUrl: 'https://via.placeholder.com/800x600/27AE60/FFFFFF?text=TypeScript+Fundamentals'
    },
  ],
  incompleteCourses: [
    {
      id: 'course-1',
      name: 'Advanced React Patterns',
      description: 'Learn advanced React patterns and best practices',
      progress: 65,
      lastAccessed: dates.today.toISOString(),
      isCompleted: false,
      estimatedHours: 8,
    },
    {
      id: 'course-2',
      name: 'Node.js Backend Development',
      description: 'Build scalable backend applications with Node.js',
      progress: 30,
      lastAccessed: dates.yesterday.toISOString(),
      isCompleted: false,
      estimatedHours: 12,
    },
    {
      id: 'course-3',
      name: 'Database Design Principles',
      description: 'Master database design and optimization',
      progress: 15,
      lastAccessed: dates.twoDaysAgo.toISOString(),
      isCompleted: false,
      estimatedHours: 10,
    },
  ],
  };
})();

